import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { message, Modal, Spin, Empty } from 'antd';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const GOLD = '#B8892A';
const TEAL = '#1A6B54';
const INK = '#18140C';
const MUTED = '#685840';
const BG = '#FAF8F5';
const BD = '#E8E0D0';
const RED = '#C0392B';
const GREEN = '#27AE60';

const STATUS_KEYS = {
  idle:   { key: 'live.notStarted', color: MUTED, bg: '#F0EDE8' },
  live:   { key: 'live.streaming', color: '#fff', bg: RED },
  paused: { key: 'live.paused', color: '#E67E22', bg: '#FEF5E7' },
  ended:  { key: 'live.ended', color: '#95A5A6', bg: '#F2F3F4' },
};

function formatDuration(seconds) {
  if (!seconds || seconds < 0) return '00:00:00';
  const h = String(Math.floor(seconds / 3600)).padStart(2, '0');
  const m = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
  const s = String(Math.floor(seconds % 60)).padStart(2, '0');
  return `${h}:${m}:${s}`;
}

function CopyField({ label, value, isMobile, t }) {
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value || '');
      message.success(t('common.copied'));
    } catch {
      // Fallback
      const ta = document.createElement('textarea');
      ta.value = value || '';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      message.success(t('common.copied'));
    }
  };

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, color: MUTED, marginBottom: 4 }}>{label}</div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        background: '#F4F1EC', borderRadius: 6, padding: '8px 12px',
        border: `1px solid ${BD}`,
      }}>
        <div style={{
          flex: 1, fontSize: 13, color: INK,
          wordBreak: 'break-all', lineHeight: 1.5,
          fontFamily: 'monospace',
        }}>
          {value || '--'}
        </div>
        <button
          onClick={handleCopy}
          style={{
            flexShrink: 0, background: GOLD, color: '#fff',
            border: 'none', borderRadius: 4, padding: '4px 12px',
            fontSize: 12, cursor: 'pointer', fontWeight: 500,
          }}
        >
          {t('common.copy')}
        </button>
      </div>
    </div>
  );
}

export default function LiveBroadcast() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [loading, setLoading] = useState(true);
  const [room, setRoom] = useState(null);
  const [products, setProducts] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [duration, setDuration] = useState(0);
  const [actionLoading, setActionLoading] = useState(false);
  const [showEndModal, setShowEndModal] = useState(false);
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [merchantProducts, setMerchantProducts] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const wsRef = useRef(null);
  const timerRef = useRef(null);
  const chatEndRef = useRef(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Fetch room data
  const fetchRoom = useCallback(async () => {
    try {
      const res = await api.get(`/live/${id}`);
      const data = res.data?.data || res.data;
      setRoom(data);
      setProducts(data.products || []);
      if (data.started_at && data.status === 'live') {
        const elapsed = Math.floor((Date.now() - new Date(data.started_at).getTime()) / 1000);
        setDuration(elapsed > 0 ? elapsed : 0);
      }
    } catch (e) {
      message.error(t('live.loadFailed', '加载直播间信息失败'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchRoom();
  }, [fetchRoom]);

  // Duration timer
  useEffect(() => {
    if (room?.status === 'live') {
      timerRef.current = setInterval(() => {
        setDuration(prev => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [room?.status]);

  // WebSocket for chat
  useEffect(() => {
    if (!room || room.status === 'ended') return;
    const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const wsHost = window.location.host;
    try {
      const ws = new WebSocket(`${wsProtocol}://${wsHost}/ws/live/${id}/chat`);
      wsRef.current = ws;
      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          setChatMessages(prev => [...prev.slice(-99), msg]);
        } catch {}
      };
      ws.onerror = () => {};
      ws.onclose = () => {};
    } catch {}
    return () => {
      if (wsRef.current) {
        try { wsRef.current.close(); } catch {}
      }
    };
  }, [id, room?.status]);

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Status control
  const handleStatusChange = async (newStatus) => {
    setActionLoading(true);
    try {
      await api.patch(`/live/${id}`, { status: newStatus });
      message.success(
        newStatus === 'live' ? t('live.liveStarted', '直播已开始') :
        newStatus === 'paused' ? t('live.livePaused', '直播已暂停') :
        newStatus === 'ended' ? t('live.liveEndedMsg', '直播已结束') : t('live.statusUpdated', '状态已更新')
      );
      setShowEndModal(false);
      await fetchRoom();
      if (newStatus === 'live') {
        setDuration(0);
      }
    } catch (e) {
      message.error(e.response?.data?.message || t('common.failed'));
    } finally {
      setActionLoading(false);
    }
  };

  // Product management
  const handleOpenProductPicker = async () => {
    setShowProductPicker(true);
    setPickerLoading(true);
    setSelectedProductIds([]);
    try {
      const res = await api.get('/merchants/products');
      const all = res.data?.data || res.data?.rows || [];
      // Filter out already mounted products
      const mountedIds = new Set(products.map(p => p.id || p.product_id));
      setMerchantProducts(all.filter(p => !mountedIds.has(p.id)));
    } catch {
      message.error(t('live.loadProductsFailed', '加载商品列表失败'));
    } finally {
      setPickerLoading(false);
    }
  };

  const handleAddProducts = async () => {
    if (selectedProductIds.length === 0) {
      message.warning(t('live.selectProducts', '请选择要添加的商品'));
      return;
    }
    setActionLoading(true);
    try {
      await api.post(`/live/${id}/products`, { product_ids: selectedProductIds });
      message.success(t('live.productsAdded', '商品已添加'));
      setShowProductPicker(false);
      await fetchRoom();
    } catch (e) {
      message.error(e.response?.data?.message || t('live.addFailed', '添加失败'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveProduct = async (productId) => {
    try {
      await api.delete(`/live/${id}/products/${productId}`);
      message.success(t('live.removed', '已移除'));
      setProducts(prev => prev.filter(p => (p.id || p.product_id) !== productId));
    } catch {
      message.error(t('live.removeFailed', '移除失败'));
    }
  };

  const handleToggleCurrent = async (productId, isCurrent) => {
    try {
      await api.patch(`/live/${id}/products/${productId}/current`, {
        is_current: !isCurrent,
      });
      setProducts(prev => prev.map(p => {
        const pid = p.id || p.product_id;
        if (pid === productId) return { ...p, is_current: !isCurrent };
        // Only one can be current at a time
        if (!isCurrent) return { ...p, is_current: false };
        return p;
      }));
    } catch {
      message.error(t('common.failed'));
    }
  };

  if (loading) {
    return (
      <div style={{ background: BG, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!room) {
    return (
      <div style={{ background: BG, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Empty description={t('live.roomNotFound', '直播间不存在')} />
      </div>
    );
  }

  const statusDef = STATUS_KEYS[room.status] || STATUS_KEYS.idle;
  const statusInfo = { label: t(statusDef.key), color: statusDef.color, bg: statusDef.bg };
  const stats = room.stats || {};

  return (
    <div style={{ background: BG, minHeight: '100vh', paddingBottom: isMobile ? 80 : 40 }}>
      {/* Header */}
      <div style={{
        background: '#fff', borderBottom: `1px solid ${BD}`,
        padding: '16px 20px',
        display: 'flex', alignItems: 'center', gap: 12,
        maxWidth: 960, margin: '0 auto',
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 18, color: INK, padding: 0,
          }}
        >
          &larr;
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: INK }}>
            {room.title || t('live.console', '直播控制台')}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
            <span style={{
              fontSize: 11, fontWeight: 600,
              color: statusInfo.color, background: statusInfo.bg,
              padding: '1px 8px', borderRadius: 3,
            }}>
              {statusInfo.label}
            </span>
            <span style={{ fontSize: 12, color: MUTED }}>
              {room.viewer_count || 0} {t('live.watching', '观看')}
            </span>
            {room.status === 'live' && (
              <span style={{ fontSize: 12, color: MUTED, fontFamily: 'monospace' }}>
                {formatDuration(duration)}
              </span>
            )}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 960, margin: '0 auto', padding: isMobile ? '12px 12px' : '20px 24px' }}>
        {/* Push Stream Info Card */}
        <div style={{
          background: '#fff', borderRadius: 10, border: `1px solid ${BD}`,
          padding: isMobile ? 16 : 20, marginBottom: 16,
        }}>
          <div style={{
            fontSize: 15, fontWeight: 600, color: INK, marginBottom: 14,
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <span style={{
              width: 4, height: 16, background: GOLD, borderRadius: 2,
              display: 'inline-block',
            }} />
            {t('live.pushInfo', '推流信息')}
          </div>
          <CopyField label={t('live.pushUrl', '推流地址 (Push URL)')} value={room.push_url} isMobile={isMobile} t={t} />
          <CopyField label={t('live.pushKey', '推流密钥 (Push Key)')} value={room.push_key} isMobile={isMobile} t={t} />
          <div style={{
            background: '#FEFCF7', border: `1px solid #E8DCC8`,
            borderRadius: 6, padding: 12, marginTop: 8,
          }}>
            <div style={{ fontSize: 13, color: MUTED, lineHeight: 1.8 }}>
              {t('live.obsGuide', '请使用 OBS 或手机推流软件，将以上推流地址和密钥填入。')}
            </div>
            <a
              href="https://obsproject.com/download"
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: 13, color: TEAL, textDecoration: 'underline' }}
            >
              {t('live.downloadObs', '下载 OBS Studio')} &rarr;
            </a>
          </div>
        </div>

        {/* Control Buttons */}
        <div style={{
          background: '#fff', borderRadius: 10, border: `1px solid ${BD}`,
          padding: isMobile ? 16 : 20, marginBottom: 16,
        }}>
          <div style={{
            fontSize: 15, fontWeight: 600, color: INK, marginBottom: 14,
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <span style={{
              width: 4, height: 16, background: GOLD, borderRadius: 2,
              display: 'inline-block',
            }} />
            {t('live.control', '直播控制')}
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {room.status === 'idle' && (
              <button
                onClick={() => handleStatusChange('live')}
                disabled={actionLoading}
                style={{
                  flex: 1, minWidth: 120, padding: '12px 0',
                  background: GREEN, color: '#fff', border: 'none',
                  borderRadius: 8, fontSize: 15, fontWeight: 600,
                  cursor: 'pointer', opacity: actionLoading ? 0.6 : 1,
                }}
              >
                {t('live.startLive', '开始直播')}
              </button>
            )}
            {room.status === 'live' && (
              <>
                <button
                  onClick={() => handleStatusChange('paused')}
                  disabled={actionLoading}
                  style={{
                    flex: 1, minWidth: 100, padding: '12px 0',
                    background: '#F39C12', color: '#fff', border: 'none',
                    borderRadius: 8, fontSize: 15, fontWeight: 600,
                    cursor: 'pointer', opacity: actionLoading ? 0.6 : 1,
                  }}
                >
                  {t('liveBroadcast.pause')}
                </button>
                <button
                  onClick={() => setShowEndModal(true)}
                  disabled={actionLoading}
                  style={{
                    flex: 1, minWidth: 100, padding: '12px 0',
                    background: RED, color: '#fff', border: 'none',
                    borderRadius: 8, fontSize: 15, fontWeight: 600,
                    cursor: 'pointer', opacity: actionLoading ? 0.6 : 1,
                  }}
                >
                  {t('liveBroadcast.endLive')}
                </button>
              </>
            )}
            {room.status === 'paused' && (
              <>
                <button
                  onClick={() => handleStatusChange('live')}
                  disabled={actionLoading}
                  style={{
                    flex: 1, minWidth: 100, padding: '12px 0',
                    background: GREEN, color: '#fff', border: 'none',
                    borderRadius: 8, fontSize: 15, fontWeight: 600,
                    cursor: 'pointer', opacity: actionLoading ? 0.6 : 1,
                  }}
                >
                  {t('liveBroadcast.resumeLive')}
                </button>
                <button
                  onClick={() => setShowEndModal(true)}
                  disabled={actionLoading}
                  style={{
                    flex: 1, minWidth: 100, padding: '12px 0',
                    background: RED, color: '#fff', border: 'none',
                    borderRadius: 8, fontSize: 15, fontWeight: 600,
                    cursor: 'pointer', opacity: actionLoading ? 0.6 : 1,
                  }}
                >
                  {t('liveBroadcast.endLive')}
                </button>
              </>
            )}
            {room.status === 'ended' && (
              <div style={{ fontSize: 14, color: MUTED, padding: '8px 0' }}>
                {t('liveBroadcast.sessionEnded')}
              </div>
            )}
          </div>
        </div>

        {/* Two column layout for desktop */}
        <div style={{
          display: isMobile ? 'block' : 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: 16,
        }}>
          {/* Product Management */}
          <div style={{
            background: '#fff', borderRadius: 10, border: `1px solid ${BD}`,
            padding: isMobile ? 16 : 20, marginBottom: isMobile ? 16 : 0,
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              marginBottom: 14,
            }}>
              <div style={{
                fontSize: 15, fontWeight: 600, color: INK,
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <span style={{
                  width: 4, height: 16, background: GOLD, borderRadius: 2,
                  display: 'inline-block',
                }} />
                {t('liveBroadcast.liveProducts')} ({products.length})
              </div>
              {room.status !== 'ended' && (
                <button
                  onClick={handleOpenProductPicker}
                  style={{
                    background: 'none', border: `1px solid ${GOLD}`,
                    color: GOLD, borderRadius: 4, padding: '4px 12px',
                    fontSize: 12, cursor: 'pointer', fontWeight: 500,
                  }}
                >
                  + {t('liveBroadcast.addProduct')}
                </button>
              )}
            </div>

            {products.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 24, color: MUTED, fontSize: 13 }}>
                {t('liveBroadcast.noProducts')}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {products.map((p, idx) => {
                  const pid = p.id || p.product_id;
                  return (
                    <div key={pid || idx} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '8px 10px', borderRadius: 6,
                      background: p.is_current ? '#FFF9F0' : '#FAFAF8',
                      border: `1px solid ${p.is_current ? GOLD : BD}`,
                    }}>
                      {/* Thumbnail */}
                      <div style={{
                        width: 44, height: 44, borderRadius: 4,
                        background: '#E8E0D0', overflow: 'hidden', flexShrink: 0,
                      }}>
                        {p.image_url && (
                          <img src={p.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: 13, color: INK, fontWeight: 500,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {p.title || p.name || `${t('liveWatch.product')} #${idx + 1}`}
                        </div>
                        {p.price && (
                          <div style={{ fontSize: 12, color: GOLD, marginTop: 2 }}>
                            ¥{Number(p.price).toLocaleString()}
                          </div>
                        )}
                      </div>
                      {/* Current toggle */}
                      {room.status !== 'ended' && (
                        <button
                          onClick={() => handleToggleCurrent(pid, p.is_current)}
                          style={{
                            background: p.is_current ? GOLD : '#E8E0D0',
                            color: p.is_current ? '#fff' : MUTED,
                            border: 'none', borderRadius: 4,
                            padding: '3px 8px', fontSize: 11,
                            cursor: 'pointer', fontWeight: 500, flexShrink: 0,
                          }}
                        >
                          {p.is_current ? t('liveWatch.explaining') : t('liveBroadcast.explain')}
                        </button>
                      )}
                      {/* Remove */}
                      {room.status !== 'ended' && (
                        <button
                          onClick={() => handleRemoveProduct(pid)}
                          style={{
                            background: 'none', border: 'none',
                            color: '#C0392B', cursor: 'pointer',
                            fontSize: 14, padding: '0 4px', flexShrink: 0,
                          }}
                          title={t('liveBroadcast.remove')}
                        >
                          &times;
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Chat Preview + Stats */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Chat */}
            <div style={{
              background: '#fff', borderRadius: 10, border: `1px solid ${BD}`,
              padding: isMobile ? 16 : 20,
              display: 'flex', flexDirection: 'column',
            }}>
              <div style={{
                fontSize: 15, fontWeight: 600, color: INK, marginBottom: 10,
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <span style={{
                  width: 4, height: 16, background: GOLD, borderRadius: 2,
                  display: 'inline-block',
                }} />
                {t('liveBroadcast.chatMessages')}
              </div>
              <div style={{
                height: 200, overflowY: 'auto',
                background: '#FAFAF8', borderRadius: 6,
                padding: 10, border: `1px solid ${BD}`,
              }}>
                {chatMessages.length === 0 ? (
                  <div style={{
                    height: '100%', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', color: '#B0A898', fontSize: 13,
                  }}>
                    {t('notifications.empty')}
                  </div>
                ) : (
                  chatMessages.map((msg, i) => (
                    <div key={i} style={{
                      marginBottom: 6, fontSize: 13, lineHeight: 1.5,
                    }}>
                      <span style={{ color: TEAL, fontWeight: 500, marginRight: 6 }}>
                        {msg.nickname || msg.user_name || t('liveWatch.user')}
                      </span>
                      <span style={{ color: INK }}>{msg.content || msg.text}</span>
                    </div>
                  ))
                )}
                <div ref={chatEndRef} />
              </div>
            </div>

            {/* Stats */}
            <div style={{
              background: '#fff', borderRadius: 10, border: `1px solid ${BD}`,
              padding: isMobile ? 16 : 20,
            }}>
              <div style={{
                fontSize: 15, fontWeight: 600, color: INK, marginBottom: 14,
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <span style={{
                  width: 4, height: 16, background: GOLD, borderRadius: 2,
                  display: 'inline-block',
                }} />
                {t('liveBroadcast.liveStats')}
              </div>
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 12,
              }}>
                {[
                  { label: t('liveBroadcast.currentViewers'), value: room.viewer_count || stats.viewer_count || 0 },
                  { label: t('liveBroadcast.peakViewers'), value: stats.peak_viewers || 0 },
                  { label: t('liveBroadcast.totalViewers'), value: stats.total_viewers || 0 },
                  { label: t('liveBroadcast.likeCount'), value: stats.like_count || 0 },
                  { label: t('liveBroadcast.messageCount'), value: stats.message_count || 0 },
                  { label: t('liveBroadcast.duration'), value: formatDuration(duration) },
                ].map((s, i) => (
                  <div key={i} style={{
                    textAlign: 'center', padding: '10px 0',
                    background: '#FAFAF8', borderRadius: 6,
                  }}>
                    <div style={{
                      fontSize: 18, fontWeight: 700, color: INK,
                      fontFamily: typeof s.value === 'string' ? 'monospace' : 'inherit',
                    }}>
                      {typeof s.value === 'number' && s.value >= 10000
                        ? `${(s.value / 10000).toFixed(1)}${t('live.wan', '万')}`
                        : s.value}
                    </div>
                    <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* End Confirm Modal */}
      <Modal
        open={showEndModal}
        title={t('liveBroadcast.confirmEnd')}
        onCancel={() => setShowEndModal(false)}
        onOk={() => handleStatusChange('ended')}
        okText={t('liveBroadcast.confirmEndBtn')}
        cancelText={t('common.cancel')}
        okButtonProps={{ danger: true, loading: actionLoading }}
      >
        <p style={{ fontSize: 14, color: INK }}>
          {t('liveBroadcast.endConfirmMsg')}
        </p>
      </Modal>

      {/* Product Picker Modal */}
      <Modal
        open={showProductPicker}
        title={t('liveBroadcast.addLiveProduct')}
        onCancel={() => setShowProductPicker(false)}
        onOk={handleAddProducts}
        okText={`${t('liveBroadcast.add')} (${selectedProductIds.length})`}
        cancelText={t('common.cancel')}
        okButtonProps={{ disabled: selectedProductIds.length === 0, loading: actionLoading }}
        width={isMobile ? '95vw' : 520}
        bodyStyle={{ maxHeight: 400, overflowY: 'auto' }}
      >
        {pickerLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : merchantProducts.length === 0 ? (
          <Empty description={t('liveBroadcast.noAvailableProducts')} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {merchantProducts.map(p => {
              const isSelected = selectedProductIds.includes(p.id);
              return (
                <div
                  key={p.id}
                  onClick={() => {
                    setSelectedProductIds(prev =>
                      isSelected ? prev.filter(x => x !== p.id) : [...prev, p.id]
                    );
                  }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 12px', borderRadius: 6, cursor: 'pointer',
                    background: isSelected ? '#FFF9F0' : '#FAFAF8',
                    border: `1px solid ${isSelected ? GOLD : BD}`,
                    transition: 'all 0.15s',
                  }}
                >
                  {/* Checkbox indicator */}
                  <div style={{
                    width: 20, height: 20, borderRadius: 4, flexShrink: 0,
                    border: `2px solid ${isSelected ? GOLD : '#C8C0B4'}`,
                    background: isSelected ? GOLD : '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#fff', fontSize: 13, fontWeight: 700,
                  }}>
                    {isSelected ? '\u2713' : ''}
                  </div>
                  {/* Thumb */}
                  <div style={{
                    width: 40, height: 40, borderRadius: 4,
                    background: '#E8E0D0', overflow: 'hidden', flexShrink: 0,
                  }}>
                    {p.image_url && (
                      <img src={p.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 13, color: INK, fontWeight: 500,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {p.title || p.name}
                    </div>
                    {p.price && (
                      <div style={{ fontSize: 12, color: GOLD, marginTop: 1 }}>
                        ¥{Number(p.price).toLocaleString()}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Modal>
    </div>
  );
}
